/**
 * 全景场景：地图点击选挂点（WGS84）
 */
import { useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { clickToWgs84 } from '../utils/orthoToolbox';

const MARKER_ID = 'zhxt-pano-pick-marker';

export default function usePanoramaMapPick({
  getMap,
  drawingToolsActiveRef,
  setBlockEarthBlankFly,
  onStartInteract
}) {
  const [pickSession, setPickSession] = useState(null);
  // { sceneId, sceneName, pendingDisplay: [lng,lat]|null, pendingWgs: [lon,lat]|null }
  const sessionRef = useRef(null);
  const markerRef = useRef(null);
  const clickHandlerRef = useRef(null);
  const contextHandlerRef = useRef(null);

  useEffect(() => {
    sessionRef.current = pickSession;
  }, [pickSession]);

  const clearMarker = () => {
    try { markerRef.current?.remove?.(); } catch (_) { /* ignore */ }
    markerRef.current = null;
  };

  const endListeners = () => {
    const map = getMap?.();
    if (map && clickHandlerRef.current) {
      try { map.off('click', clickHandlerRef.current); } catch (_) { /* ignore */ }
    }
    if (map && contextHandlerRef.current) {
      try { map.off('contextmenu', contextHandlerRef.current); } catch (_) { /* ignore */ }
    }
    clickHandlerRef.current = null;
    contextHandlerRef.current = null;
    try {
      if (map?.getCanvas) map.getCanvas().style.cursor = '';
    } catch (_) { /* ignore */ }
  };

  const stopPick = ({ restoreBlock = true } = {}) => {
    endListeners();
    clearMarker();
    setPickSession(null);
    if (drawingToolsActiveRef) drawingToolsActiveRef.current = false;
    if (restoreBlock) setBlockEarthBlankFly?.(false);
  };

  const beginPick = ({ sceneId, sceneName } = {}) => {
    const map = getMap?.();
    if (!map) {
      message.warning('地图尚未就绪');
      return false;
    }
    onStartInteract?.();
    if (drawingToolsActiveRef) drawingToolsActiveRef.current = true;
    setBlockEarthBlankFly?.(true);

    endListeners();
    clearMarker();

    const onClick = (e) => {
      try { e.preventDefault?.(); } catch (_) { /* ignore */ }
      const display = [e.lngLat.lng, e.lngLat.lat];
      const wgs = clickToWgs84(e.lngLat, map);
      clearMarker();
      try {
        // 动态取 mapbox-gl（与指挥台一致）
        // eslint-disable-next-line global-require
        const mapboxgl = require('mapbox-gl');
        const el = document.createElement('div');
        el.id = MARKER_ID;
        el.style.width = '16px';
        el.style.height = '16px';
        el.style.borderRadius = '50%';
        el.style.background = '#2563eb';
        el.style.border = '2px solid #fff';
        el.style.boxShadow = '0 1px 6px rgba(0,0,0,0.35)';
        markerRef.current = new mapboxgl.Marker({ element: el })
          .setLngLat(display)
          .addTo(map);
      } catch (_) {
        /* 无 Marker 也能确认坐标 */
      }
      setPickSession((prev) => (prev ? {
        ...prev,
        pendingDisplay: display,
        pendingWgs: wgs
      } : prev));
    };

    const onContext = (e) => {
      try { e.preventDefault(); } catch (_) { /* ignore */ }
    };

    clickHandlerRef.current = onClick;
    contextHandlerRef.current = onContext;
    map.on('click', onClick);
    map.on('contextmenu', onContext);
    try { map.getCanvas().style.cursor = 'crosshair'; } catch (_) { /* ignore */ }

    setPickSession({
      sceneId,
      sceneName: sceneName || '',
      pendingDisplay: null,
      pendingWgs: null
    });
    message.info('请在地图上点击选择挂点，然后点「确认选点」');
    return true;
  };

  const confirmPick = () => {
    const s = sessionRef.current;
    if (!s?.pendingWgs) {
      message.warning('请先在地图上点击选点');
      return null;
    }
    const result = {
      sceneId: s.sceneId,
      lon: Number(s.pendingWgs[0]),
      lat: Number(s.pendingWgs[1])
    };
    stopPick();
    return result;
  };

  const cancelPick = () => {
    stopPick();
    message.info('已取消地图选点');
  };

  useEffect(() => () => stopPick({ restoreBlock: true }), []);

  return {
    pickSession,
    beginPick,
    confirmPick,
    cancelPick,
    isPicking: !!pickSession
  };
}
